<template>
  <v-row justify="center">
    <v-col cols="11" sm="8" md="6" lg="5" xl="4" class="pt-8">
      <v-card class="pa-5" outlined>
        <v-card-title>
          <h1>Owner Login</h1>
        </v-card-title>
        <v-card-text>
          <div v-if="showLoggedInForm">
            <v-alert type="success" text class="pa-2 my-6 text-h6"
              >You're currently logged in.</v-alert
            >
            <v-btn
              color="blue darken-3"
              class="white--text mb-4"
              :loading="loading"
              depressed
              x-large
              block
              :to="{ name: ManageShopSettingsRoute }"
              >Manage My Shop</v-btn
            >
            <v-btn
              color="blue darken-3"
              class="white--text mb-4"
              :loading="loading"
              depressed
              large
              outlined
              block
              @click="logoutHandler"
              >Logout</v-btn
            >
          </div>
          <v-form
            v-else
            @submit.prevent="maybeLogin"
            v-model="isFormValid"
            class="admin-login-form"
          >
            <v-alert v-if="getIsShopDead" type="info" text class="pa-2 my-2"
              >To reactivate your shop, sign in below and subscribe from your
              Shop Settings.</v-alert
            >
            <v-alert
              v-else-if="$route.query.loggedOut"
              type="success"
              dense
              text
              class="pa-2 my-2"
              >You've been logged out.</v-alert
            >
            <h3 v-else>Please login to edit your store settings</h3>
            <v-text-field
              v-model="email"
              class="email-input mt-4"
              color="deep-orange"
              label="Email Address"
              large
              :rules="[emailRule]"
              ref="email"
              autofocus
            />
            <v-text-field
              v-model="password"
              class="password-input mt-4"
              color="deep-orange"
              label="Password"
              large
              :rules="[requiredRule]"
              ref="password"
              type="password"
            />
            <transition name="fade" mode="out-in">
              <div v-if="error" class="red--text mb-2">{{ error }}</div>
            </transition>
            <v-btn
              color="amber darken-3"
              :disabled="!isFormValid || needsActivation || isSendingResetLink"
              class="white--text mb-4"
              :loading="loading"
              depressed
              x-large
              block
              type="submit"
              >Login</v-btn
            >

            <div class="text-center my-4">
              <v-btn small plain :to="{ name: ForgotEmailRoute }"
                >Forgot Email?</v-btn
              >
              &middot;
              <v-btn small right plain :to="{ name: ForgotPasswordRoute }"
                >Forgot Password?</v-btn
              >
            </div>

            <SendPasswordResetButton
              v-if="error"
              :email="email"
              @sending="isSendingResetLink = true"
              @done="isSendingResetLink = false"
              @error="error = $event"
            />

            <!-- <h3 class="mt-4">Don't have an Admin account yet?</h3>
            <v-btn
              color="blue"
              depressed
              exact
              class="white--text mt-2"
              :disabled="loading"
              to="/admin/signup"
              >Register Account</v-btn
            > -->
          </v-form>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { USER_NOT_ACTIVATED } from '@/constants/ApiMessages'
import { MerchantRoutes, CustomerRoutes } from '@/constants/RouteNames'
import SendPasswordResetButton from '@/components/SendPasswordResetButton'
import _get from 'lodash/get'

export default {
  name: 'MerchantLoginView',
  components: { SendPasswordResetButton },
  data: () => {
    return {
      isFormValid: false,
      loading: false,
      error: false,
      isSendingResetLink: false,
      password: '',
      email: '',
      showLoggedInForm: false,
      requiredRule: (v) => !!v.trim() || 'This field is required',
      emailRule: (email) => {
        var re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        return re.test(email) || 'Please enter a valid email'
      },
      errorMessages: {
        [USER_NOT_ACTIVATED]: `This user account has not yet been activated. Please check your email for the activation link or email hi@getminimart.com for help.`,
      },
      ManageShopSettingsRoute: MerchantRoutes.Manage.SHOP_SETTINGS,
      OwnerDashboardRoute: MerchantRoutes.Manage.DASHBOARD,
      ManageOrdersRoute: MerchantRoutes.Manage.ORDERS,
      ForgotPasswordRoute: CustomerRoutes.FORGOT_PASSWORD,
      ForgotEmailRoute: MerchantRoutes.FORGOT_EMAIL,
    }
  },
  created() {
    this.showLoggedInForm = this.canMerchant
  },
  mounted() {
    if (!this.$refs.email) return
    this.$refs.email.$el.focus()
    this.email = this.$route.query.email || ''
  },
  methods: {
    ...mapActions('auth', ['merchantLogin', 'logout']),

    async maybeLogin() {
      this.loading = true
      this.error = false
      this.wasPasswordEmailSent = false
      let { email, password } = this

      try {
        let admin = await this.merchantLogin({ email, password })
        if (admin) {
          if (this.getIsShopDead) {
            await this.$router.push({
              name: this.ManageShopSettingsRoute,
              hash: '#subscription',
            })
          } else {
            const nextRouteName = _get(
              this.$route,
              'query.next',
              this.OwnerDashboardRoute
            )

            if (this.$route.query.redirect) {
              this.$router.push({
                path: this.$route.query.redirect,
                hash: this.$route.hash,
              })
            } else {
              await this.$router.push({ name: nextRouteName })
            }
          }
        }
      } catch (error) {
        if (_get(error, 'response.status') === 401) {
          this.error = `Sorry but the email or password you entered was incorrect. Please try again.`
        } else {
          this.error = _get(
            error,
            'response.data.error',
            `Sorry but that didn't work. Please try again.`
          )
        }
        this.loading = false
      }
    }, // maybeLogin
    beforeDestroy() {
      this.loading = false
    },

    async logoutHandler() {
      await this.logout()
      this.showLoggedInForm = false
    },
  },
  computed: {
    ...mapGetters('auth', ['canMerchant']),
    ...mapGetters('shop', ['getIsShopDead']),
    needsActivation() {
      return this.error === this.errorMessages[USER_NOT_ACTIVATED]
    },
  },
  beforeDestroy() {
    this.loading = false
    this.error = false
  },
}
</script>

<style lang="css">
.admin-login-form input {
  font-size: 2em;
  line-height: 1.3;
  max-height: none;
}
</style>
